import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Plus, Trash2, Edit, Shield, UserCheck, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  admin: { label: 'Admin', icon: Shield, color: 'bg-red-100 text-red-700 border-red-200' },
  partner: { label: 'Partner', icon: Briefcase, color: 'bg-violet-100 text-violet-700 border-violet-200' },
  employee: { label: 'Employee', icon: UserCheck, color: 'bg-blue-100 text-blue-700 border-blue-200' },
};

const RoleBadge = ({ role }) => {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.employee;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-sm border ${cfg.color}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const UserFormModal = ({ user, clients, onSave, onClose }) => {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'employee',
    assigned_clients: user?.assigned_clients || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return setError('Name and email are required');
    if (!isEdit && !form.password.trim()) return setError('Password is required');
    setSaving(true);
    setError('');
    try {
      await onSave(form, user?.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const toggleClient = (clientId) => {
    setForm(prev => ({
      ...prev,
      assigned_clients: prev.assigned_clients.includes(clientId)
        ? prev.assigned_clients.filter(c => c !== clientId)
        : [...prev.assigned_clients, clientId],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="user-form-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">{isEdit ? 'Edit User' : 'Add User'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{error}</div>}
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Name</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" placeholder="Full name" data-testid="user-form-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" placeholder="user@example.com" data-testid="user-form-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</label>
            <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" placeholder={isEdit ? 'Leave blank to keep current' : 'Password'}
              data-testid="user-form-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm" data-testid="user-form-role">
              <option value="admin">Admin (Full access)</option>
              <option value="partner">Partner (Full access)</option>
              <option value="employee">Employee (View assigned clients)</option>
            </select>
          </div>

          {form.role === 'employee' && (
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Assign Clients ({form.assigned_clients.length} selected)</label>
              <div className="border border-slate-200 rounded-sm max-h-[200px] overflow-y-auto">
                {clients.length === 0 ? (
                  <p className="text-xs text-slate-400 p-3">No clients available</p>
                ) : clients.map(c => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0">
                    <input type="checkbox" checked={form.assigned_clients.includes(c.id)} onChange={() => toggleClient(c.id)}
                      className="rounded border-slate-300" />
                    <span className="text-sm truncate">{c.name}</span>
                    <span className="text-xs text-slate-400 font-mono ml-auto">{c.gstin}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="text-sm rounded-sm">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm rounded-sm" data-testid="user-form-submit">
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create User')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/clients`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function createNewUser(form) {
    await axios.post(`${BACKEND_URL}/api/users`, form, { withCredentials: true });
    await fetchData();
  }

  async function updateExistingUser(targetId, form) {
    await axios.put(`${BACKEND_URL}/api/users/${targetId}`, form, { withCredentials: true });
    await fetchData();
  }

  async function saveUser(form, targetId) {
    return targetId ? updateExistingUser(targetId, form) : createNewUser(form);
  }

  async function removeUser(id) {
    await axios.delete(`${BACKEND_URL}/api/users/${id}`, { withCredentials: true });
    setDeleteId(null);
    await fetchData();
  }

  if (currentUser?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Access denied. Admin only.</p>
      </div>
    );
  }

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.name]));

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
            <p className="text-sm text-slate-500">{users.length} users</p>
          </div>
          <Button onClick={() => { setEditUser(null); setShowForm(true); }} className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm" data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add User
          </Button>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 py-12">Loading...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase px-4 py-3">Name</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase px-4 py-3">Email</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase px-4 py-3">Role</th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase px-4 py-3">Assigned Clients</th>
                  <th className="text-right text-xs font-semibold text-slate-500 uppercase px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">{u.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">{u.email}</td>
                    <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {u.role === 'employee' ? (
                        (u.assigned_clients || []).length > 0
                          ? (u.assigned_clients || []).map(cid => clientMap[cid] || cid).join(', ')
                          : <span className="text-slate-300">None assigned</span>
                      ) : (
                        <span className="text-slate-300">All clients</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setEditUser(u); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-[#0055FF]" data-testid={`edit-user-${u.id}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        {u.id !== currentUser?.id && (
                          <button onClick={() => setDeleteId(u.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showForm && (
          <UserFormModal user={editUser} clients={clients} onSave={saveUser} onClose={() => { setShowForm(false); setEditUser(null); }} />
        )}

        {deleteId && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
            <div className="bg-white rounded-md shadow-xl p-6 w-full max-w-sm mx-4">
              <h3 className="font-semibold text-slate-800 mb-2">Delete User</h3>
              <p className="text-sm text-slate-500 mb-4">Are you sure? This action cannot be undone.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteId(null)} className="text-sm rounded-sm">Cancel</Button>
                <Button onClick={() => removeUser(deleteId)} className="bg-red-600 hover:bg-red-700 text-white text-sm rounded-sm" data-testid="confirm-delete-user">Delete</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
